import { addDays, addHours, endOfDay, isEqual, nextMonday, startOfDay } from 'date-fns';

export const formatTimestampToYYYYMMDD = (timestamp?: number): string | undefined => {
  if (timestamp === undefined) {
    return undefined;
  }
  const d = new Date(timestamp);
  return '' + d.getFullYear() + ('0' + (d.getMonth() + 1)).slice(-2) + ('0' + d.getDate()).slice(-2);
};

// UTC version, used to compare self-service API future dates such as invoice "due"
export const formatTimestampToUTCYYYYMMDD = (timestamp?: number): string | undefined => {
  if (timestamp === undefined) {
    return undefined;
  }
  const d = new Date(timestamp);
  return '' + d.getUTCFullYear() + ('0' + (d.getUTCMonth() + 1)).slice(-2) + ('0' + d.getUTCDate()).slice(-2);
};

const formatToDDMMYYYY = (timestamp: number): string => {
  const d = new Date(timestamp);
  return `${d.getDate()}.${d.getMonth() + 1}.${d.getFullYear()}`;
};

export const formatToDDMM = (timestamp: number): string => {
  const d = new Date(timestamp);
  return `${d.getDate()}.${d.getMonth() + 1}.`;
};

export const formatTimestampToDDMMYYYY = (timestamp?: number): string | undefined => {
  if (timestamp === undefined) {
    return undefined;
  }
  return formatToDDMMYYYY(timestamp);
};

export const formatDefinedTimestampToDDMMYYYY = (timestamp: number): string => {
  return formatToDDMMYYYY(timestamp);
};

// UTC version, used in the self-service API for future dates such as invoice "due"
export const formatTimestampToUTCDDMMYYYY = (timestamp?: number): string | undefined => {
  if (timestamp === undefined) {
    return undefined;
  }
  const d = new Date(timestamp);
  return `${d.getUTCDate()}.${d.getUTCMonth() + 1}.${d.getUTCFullYear()}`;
};

// UTC version for cases when the timestamp is already in local time zone, e.g. calldetails-api
export const formatTimestampToUTCDDMMYYYYHHmm = (timestamp?: number): string | undefined => {
  if (!timestamp) {
    return undefined;
  }
  const d = new Date(timestamp);
  const date = `${d.getUTCDate()}.${d.getUTCMonth() + 1}.${d.getUTCFullYear()}`;
  const time = `${d.getUTCHours()}:${('0' + d.getUTCMinutes()).slice(-2)}`;
  return `${date} ${time}`;
};

export const formatTimeToYYYYMMDDHHmm = (timestamp: string) => {
  const d = new Date(timestamp);
  const date = `${d.getUTCDate()}.${d.getUTCMonth() + 1}.${d.getUTCFullYear()}`;
  const time = `${d.getHours()}:${('0' + d.getMinutes()).slice(-2)}:${('0' + d.getSeconds()).slice(-2)}`;
  return `${date} ${time}`;
};

export const formatTimeStampToYYYYMMDDHHmm = (timestamp: number): string => {
  const d = new Date(timestamp);
  const date = `${d.getUTCDate()}.${d.getUTCMonth() + 1}.${d.getUTCFullYear()}`;
  const time = `${d.getHours()}:${('0' + d.getMinutes()).slice(-2)}`;
  return `${date} ${time}`;
};

export const formatTimeStampToHHmm = (timestamp: number): string => {
  const d = new Date(timestamp);
  return `${('0' + d.getHours()).slice(-2)}:${('0' + d.getMinutes()).slice(-2)}`;
};

export const isTimestampDateInFuture = (timestamp: number, timeStampToCompare?: number): boolean =>
  timeStampToCompare ? timeStampToCompare < timestamp : Date.now() < timestamp;

export const isWeekend = (date: Date): boolean => [0, 6].some(day => day === date.getDay());

/**
 * Returns Date without hours, minutes, seconds and millis.
 * @param time number, additionally can specify milliseconds of time of date.
 */
export const getNormalizedDate = (time?: number): Date => {
  let date;
  if (time) {
    date = new Date(time);
  } else {
    date = new Date();
  }
  date.setHours(0);
  date.setMinutes(0);
  date.setSeconds(0);
  date.setMilliseconds(0);

  return date;
};

export const getNormalizedDateFromDateString = (dateStr: string) => {
  const [day, month, year] = dateStr.split('.');
  return getNormalizedDate(new Date(`${year}-${month}-${day}`).getTime());
};

export const nextWeekday = (date: Date): Date => {
  const nextAvailableDate = addDays(date, 1);
  return isWeekend(nextAvailableDate) ? nextMonday(nextAvailableDate) : nextAvailableDate;
};

export const setNextWeekdayIfWeekend = (date: Date) => {
  if (isWeekend(date)) {
    date.setTime(nextWeekday(date).getTime());
  }
};

export const getNormalizedAdjustedDate = (
  daysToAdd?: number,
  monthsToAdd?: number,
  yearsToAdd?: number,
  shouldSetNextWeekdayIfWeekend?: boolean
) => {
  const date = getNormalizedDate();
  if (yearsToAdd) {
    date.setFullYear(date.getFullYear() + yearsToAdd);
  }
  if (monthsToAdd) {
    date.setMonth(date.getMonth() + monthsToAdd);
  }
  if (daysToAdd) {
    date.setDate(date.getDate() + daysToAdd);
  }

  if (shouldSetNextWeekdayIfWeekend) {
    setNextWeekdayIfWeekend(date);
  }

  return date;
};

export const getLocalizedDateWithLeadingZeroes = (date: Date, locale: string) =>
  date.toLocaleDateString(locale, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });

export const getLocalizedDateString = (date: Date) => `${date.getDate()}.${date.getMonth() + 1}.${date.getFullYear()}`;

export const isAllowedTransferDay = (date: Date, blockedDays: Date[]) => {
  const startOfTheDay = startOfDay(date);
  return (
    startOfTheDay.getTime() > addHours(endOfDay(new Date()), -1).getTime() &&
    !isWeekend(startOfTheDay) &&
    !blockedDays.some(blockedDay => isEqual(startOfDay(blockedDay), startOfTheDay))
  );
};

export const getNextAllowedDay = (blockedDays: Date[]) => {
  let currentDate = startOfDay(new Date());
  let i = 0;
  while (i <= 5) {
    currentDate = addDays(currentDate, 1);
    if (isAllowedTransferDay(currentDate, blockedDays)) {
      i++;
    }
  }

  return currentDate;
};

export const validateScheduledChangeDate = (
  value: string,
  maxDaysAhead: number,
  errorMessage: string
): string | undefined => {
  if (!value) {
    return undefined;
  }
  const date = getNormalizedDateFromDateString(value);
  if (Number.isNaN(date.getTime())) {
    return errorMessage;
  }
  const tomorrow = getNormalizedDate();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const maxDate = getNormalizedDate();
  maxDate.setDate(maxDate.getDate() + maxDaysAhead);
  if (date < tomorrow || date > maxDate) {
    return errorMessage;
  }
  return undefined;
};

type DatePickerDate = [number, number, number];

const parseDateParts = (year: number, month: number, day: number): DatePickerDate | undefined => {
  const date = new Date(0);
  date.setUTCHours(0, 0, 0, 0);
  date.setUTCFullYear(year, month - 1, day);
  return date.getUTCFullYear() === year && date.getUTCMonth() + 1 === month && date.getUTCDate() === day
    ? [year, month, day]
    : undefined;
};

export const parseIsoDate = (value: string): DatePickerDate | undefined => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  return match ? parseDateParts(Number(match[1]), Number(match[2]), Number(match[3])) : undefined;
};

export const toIsoDate = (value: string): string | undefined => {
  if (!value.trim()) {
    return '';
  }
  const match = /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/.exec(value);
  if (!match) {
    return undefined;
  }
  const date = parseDateParts(Number(match[3]), Number(match[2]), Number(match[1]));
  return date
    ? `${date[0].toString().padStart(4, '0')}-${date[1].toString().padStart(2, '0')}-${date[2].toString().padStart(2, '0')}`
    : undefined;
};

export const onDateValueChange = (
  value: string | { rangeStart: string; rangeEnd: string },
  param: string,
  onChange: (param: string, value: string) => void
) => {
  if (typeof value === 'string') {
    const isoDate = toIsoDate(value);
    if (isoDate !== undefined) {
      onChange(param, isoDate ?? '');
    }
  }
};
